import { useGLTF } from '@react-three/drei';
import { BufferGeometry } from 'three';
import type { Material } from 'three';
import React, { useContext, useEffect, useMemo } from 'react';

import { useSecureModelURL } from '../../secureRemoteFiles';
import {
  topLevelTypeFor,
  machinePlacement,
  machinePlacementKey,
  machineMesh,
  machineBox,
  createPlaceholderGeometry,
  createPlaceholderHitBox,
} from '@repo/ui';
import type { MachinePlacement } from '@repo/ui';
import { Entity, TwinEntityType, AppMode } from '@repo/frontend-types';
import { TwinEntity } from '@repo/backend-types';
import { Box } from '../../behaviours/Box';
import { EditView } from '../../behaviours/EditView';
import { useModeContext, useTwinContext } from '@repo/front-end-state';
import { MachineMaterialContext, MachineRenderOrderContext } from './MachineMaterialContext';
import { MachineOverlayContext } from './MachineOverlayContext';
import { PlanEditorMachine } from './PlanEditorMachine';

type TempEquipmentModelsProps = {
  entity: Entity;
  mapDiv: HTMLDivElement;
  lineage: TwinEntity[];
};

type EquipmentModelsProps = TempEquipmentModelsProps & {
  loadingList: Map<string, boolean>;
  modelFileName: string;
};

/**
 * Where a machine sits and how it turns, from the shared rule (`@repo/ui` `machinePlacement`),
 * recomputed only when its placement inputs change. The machine turns by its saved rotation.
 */
const useMachinePlacement = (entity: Entity): MachinePlacement => {
  const placementKey = machinePlacementKey(entity);
  return useMemo(
    () => machinePlacement(entity),
    // The key is exactly the entity's placement inputs, so a new entity object that places the
    // same way reuses the placement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placementKey],
  );
};

/**
 * EquipmentModels Component
 *
 * Draws one machine from its loaded model: the mesh `machineMesh` picks, placed by
 * `machinePlacement`, with the box `machineBox` builds on that mesh as it is drawn (mirror applied).
 */
const AssetImpl = ({
  entity,
  mapDiv,
  loadingList,
  modelFileName,
  lineage,
}: EquipmentModelsProps) => {
  const modelURL = useSecureModelURL(modelFileName);

  // Suspends while loading and throws on failure; `HandleAsset`'s `MachineBoundary` catches it.
  const gltf = useGLTF(modelURL);
  const nodes = gltf.nodes;

  const modelGeometry = useMemo(() => machineMesh(nodes), [nodes]);

  useEffect(() => {
    const alreadyLoaded = loadingList.get(modelFileName);
    if (modelGeometry && !alreadyLoaded) {
      loadingList.set(modelFileName, true);
    }
  }, [modelGeometry, modelFileName, loadingList]);

  if (!modelGeometry) {
    throw new Error(`The ${entity.name} asset with id ${entity.id} and bID ${entity.bID} does not have any meshes. 
			             We don't know how to display it`);
  }

  // After the mesh check, so a machine with neither a mesh nor a location reports the missing mesh,
  // as it always has. (Hooks after a throw are fine: the render is abandoned.)
  const placement = useMachinePlacement(entity);

  // The box sits on the mesh as drawn, so it takes the mesh's mirror (the box itself is not scaled).
  const [mirrorX, , mirrorZ] = placement.mirrorScale;
  const hitBox = useMemo(
    () => machineBox(modelGeometry, [mirrorX, 1, mirrorZ]),
    [modelGeometry, mirrorX, mirrorZ],
  );

  // The plan's outline takes the mesh's mirror as the mesh does (`PlanEditorMachine`).
  const mirror = useMemo(() => [mirrorX, mirrorZ] as const, [mirrorX, mirrorZ]);

  // Cleanup the box when it is replaced or the component unmounts (the model's own geometry belongs
  // to the loaded model and is not disposed here)
  useEffect(() => {
    return () => {
      hitBox.dispose();
    };
  }, [hitBox]);

  // Memoised so `Model` can compare it by reference: a re-render of this component with the same
  // props keeps the overlay the same element. None where the host draws plain machines (the 3D plan);
  // the 3D plan editor's outlines and registration (`PlanEditorMachine`).
  const overlayMode = useContext(MachineOverlayContext);
  const overlay = useMemo(
    () =>
      overlayMode === 'live' ? (
        <OverlaySwitch entity={entity} mapDiv={mapDiv} lineage={lineage} boxGeometry={hitBox} />
      ) : overlayMode === 'planEditor' ? (
        <PlanEditorMachine
          entity={entity}
          hitBox={hitBox}
          geometry={modelGeometry}
          mirror={mirror}
          modelName={modelFileName}
        />
      ) : null,
    [overlayMode, entity, mapDiv, lineage, hitBox, modelGeometry, mirror, modelFileName],
  );

  const material = useContext(MachineMaterialContext);
  const renderOrder = useContext(MachineRenderOrderContext);

  return (
    <Model
      bID={entity.bID}
      placement={placement}
      modelGeometry={modelGeometry}
      material={material}
      renderOrder={renderOrder}
    >
      {overlay}
    </Model>
  );
};

/** What a placeholder's outline is named by in the plan's warnings and measures. */
const PLACEHOLDER_MODEL_NAME = 'placeholder';

/**
 * TempEquipmentModels Component
 *
 * A bit like the models above but it creates a placeholder box, with the same overlay a model gets
 * (hover, select, label card, Edit-mode handle). Used when a machine has no model name, its model is
 * reported missing, or its model fails to load or holds no mesh.
 */
const TempAsset = ({ entity, mapDiv, lineage }: TempEquipmentModelsProps) => {
  const { placeHolderGeometry, hitBox } = useMemo(
    () => ({
      placeHolderGeometry: createPlaceholderGeometry(false),
      hitBox: createPlaceholderHitBox(),
    }),
    [],
  );

  // Cleanup geometries when component unmounts
  useEffect(() => {
    return () => {
      if (placeHolderGeometry) {
        placeHolderGeometry.dispose();
      }
      if (hitBox) {
        hitBox.dispose();
      }
    };
  }, [placeHolderGeometry, hitBox]);

  const placement = useMachinePlacement(entity);
  const [mirrorX, , mirrorZ] = placement.mirrorScale;
  const mirror = useMemo(() => [mirrorX, mirrorZ] as const, [mirrorX, mirrorZ]);

  // Memoised on the same inputs as `Asset`'s, so `Model` keeps it by reference. The placeholder is
  // the mesh this machine draws, so it is what the plan outlines (`plan-machine-outline`, R1.5).
  const overlayMode = useContext(MachineOverlayContext);
  const overlay = useMemo(
    () =>
      overlayMode === 'live' ? (
        <OverlaySwitch entity={entity} mapDiv={mapDiv} lineage={lineage} boxGeometry={hitBox} />
      ) : overlayMode === 'planEditor' ? (
        <PlanEditorMachine
          entity={entity}
          hitBox={hitBox}
          geometry={placeHolderGeometry}
          mirror={mirror}
          modelName={PLACEHOLDER_MODEL_NAME}
        />
      ) : null,
    [overlayMode, entity, mapDiv, lineage, hitBox, placeHolderGeometry, mirror],
  );

  const material = useContext(MachineMaterialContext);
  const renderOrder = useContext(MachineRenderOrderContext);

  return (
    <Model
      bID={entity.bID}
      placement={placement}
      modelGeometry={placeHolderGeometry}
      material={material}
      renderOrder={renderOrder}
    >
      {overlay}
    </Model>
  );
};

interface ModelProps {
  bID: Entity['bID'];
  placement: MachinePlacement;
  modelGeometry: BufferGeometry;
  /**
   * `MachineMaterialContext`'s: the renderer's patched copy (its `MachineLookScope`), or Build floors'
   * copy of that; the shared `MACHINE_MATERIAL` only outside any scope.
   */
  material: Material;
  /** `MachineRenderOrderContext`'s: 0 unless a 3D plan orders its machines. */
  renderOrder: number;
  children?: React.ReactNode;
}

/**
 * One machine's group and mesh. Free of app mode, stores and other contexts, so a mode change never
 * re-renders it: everything it draws arrives as props.
 */
const ModelImpl = ({
  bID,
  placement,
  modelGeometry,
  material,
  renderOrder,
  children,
}: ModelProps) => (
  <group
    renderOrder={renderOrder}
    rotation={[0, placement.rotationY, 0]}
    position={placement.position}
    scale={[1, 1, 1]}
    // Tag this group so flyToTarget's scene traversal can locate the rendered
    // asset by BID and read its world-space bounding box for precise framing.
    userData={{ bID }}
  >
    <mesh
      geometry={modelGeometry}
      castShadow
      receiveShadow
      material={material}
      position={[0, 0, 0]}
      scale={placement.mirrorScale}
    />
    {children}
  </group>
);

const sameTuple = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const samePlacement = (a: MachinePlacement, b: MachinePlacement) =>
  a === b ||
  (a.rotationY === b.rotationY &&
    sameTuple(a.position, b.position) &&
    sameTuple(a.mirrorScale, b.mirrorScale));

/** Placement by value; geometry, material and overlay by reference. */
const Model = React.memo(
  ModelImpl,
  (prev, next) =>
    prev.bID === next.bID &&
    samePlacement(prev.placement, next.placement) &&
    prev.modelGeometry === next.modelGeometry &&
    prev.material === next.material &&
    prev.renderOrder === next.renderOrder &&
    prev.children === next.children,
);

/**
 * The memo rule for a machine on the drawing path (`SmartAsset` and `Asset`): skip the render only
 * when the machine's id, model and loading list are unchanged AND nothing that places it changed
 * (`machinePlacementKey`) — so a machine with no stable id, like a plan machine, still moves.
 */
export const sameMachine = (
  prev: { entity: Entity; modelFileName: string; loadingList: Map<string, boolean> },
  next: { entity: Entity; modelFileName: string; loadingList: Map<string, boolean> },
) =>
  prev.entity.id === next.entity.id &&
  prev.modelFileName === next.modelFileName &&
  prev.loadingList === next.loadingList &&
  (prev.entity === next.entity ||
    machinePlacementKey(prev.entity) === machinePlacementKey(next.entity));

export const Asset = React.memo(AssetImpl, sameMachine);

export { TempAsset };

// Overlay rendered alongside the model, may respond to app mode changes without remounting the mesh
function OverlaySwitch({
  entity,
  mapDiv,
  lineage,
  boxGeometry,
}: {
  entity: Entity;
  mapDiv: HTMLDivElement;
  lineage: TwinEntity[];
  boxGeometry: BufferGeometry;
}) {
  const { appMode } = useModeContext();
  const { hasFloorLevel, multifloor } = useTwinContext();

  return (
    <group position={[0, (entity.depth ?? 0) / 4000, 0]}>
      {appMode === AppMode.EDIT ? (
        <EditView mapDiv={mapDiv} lineage={lineage} entity={entity} geometry={boxGeometry} />
      ) : (
        <Box
          mapDiv={mapDiv}
          lineage={lineage}
          entity={entity}
          geometry={boxGeometry}
          entityType={TwinEntityType.ASSET}
          labelBoundingBox={boxGeometry.boundingBox!}
          topLevelType={topLevelTypeFor({ hasFloorLevel, multifloor })}
        />
      )}
    </group>
  );
}
